import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { ROUTES, serviceRequestDetail, serviceRequestEdit } from '../../../constants/routes';
import axios from 'axios';
import { userHasFn, SUB_APPS } from '../../../utils/rbac';
import SubAppHeaderActions from '../../../components/SubAppHeaderActions';

function Dashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    property: '',
    status: '',
    tech: '',
    location: ''
  });
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    fetchRequests();
    fetchProperties();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filters]);

  const applyFilters = () => {
    let filtered = [...requests];

    if (filters.property) {
      filtered = filtered.filter(request => 
        request.property_id?.toString() === filters.property
      );
    }

    if (filters.status) {
      filtered = filtered.filter(request => 
        request.status?.toLowerCase().includes(filters.status.toLowerCase())
      );
    }

    if (filters.tech) {
      filtered = filtered.filter(request => 
        request.tech_name?.toLowerCase().includes(filters.tech.toLowerCase())
      );
    }

    if (filters.location) {
      filtered = filtered.filter(request => 
        request.location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    setFilteredRequests(filtered);
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/requests', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequests(response.data);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      setError('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/api/properties', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setProperties(response.data);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      property: '',
      status: '',
      tech: '',
      location: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'badge bg-warning';
      case 'in progress':
        return 'badge bg-info';
      case 'closed':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'badge bg-danger';
      case 'medium':
        return 'badge bg-warning';
      case 'low':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name : 'Unknown Property';
  };

  return (
    <div className="container mt-4 app-shell-page">
      <div className="row">
        <div className="col-12">
          <div className="app-page-header d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h1 className="h3 mb-1">Service Request Dashboard</h1>
              <p className="text-muted small mb-0">Create requests and track status across properties.</p>
            </div>
            <SubAppHeaderActions
              subAppHomePath={ROUTES.SERVICE_REQUESTS}
              subAppHomeLabel="Service Requests"
              navButtonClassName="btn btn-sm bg-white text-dark border"
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-4">
          <div className="card app-card">
            <div className="card-body">
              <h5 className="card-title">Create New Request</h5>
              <p className="card-text">Submit a new service request.</p>
              <button 
                onClick={() => navigate(ROUTES.SERVICE_REQUESTS_NEW)} 
                className="btn btn-success"
              >
                Create Request
              </button>
            </div>
          </div>
        </div>

        {userHasFn(user, SUB_APPS.HOME_ADMIN, 'panel') && (
          <div className="col-md-6 mb-4">
            <div className="card app-card">
              <div className="card-body">
                <h5 className="card-title">Admin Panel</h5>
                <p className="card-text">Manage users and all service requests.</p>
                <button 
                  onClick={() => navigate(ROUTES.ADMIN)} 
                  className="btn btn-warning"
                >
                  Admin Panel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card app-card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">Service Requests</h5>
                <button 
                  onClick={fetchRequests} 
                  className="btn btn-outline-primary btn-sm"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              
              {error && (
                <div className="alert alert-danger mb-3">
                  {error}
                </div>
              )}

              {/* Filters */}
              <div className="row mb-3">
                <div className="col-md-2">
                  <label className="form-label">Property</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.property}
                    onChange={(e) => handleFilterChange('property', e.target.value)}
                  >
                    <option value="">All Properties</option>
                    {properties.map(property => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Status</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filter by status..."
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Tech</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filter by tech..."
                    value={filters.tech}
                    onChange={(e) => handleFilterChange('tech', e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filter by location..."
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                  />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Results count */}
              <div className="mb-2">
                <small className="text-muted">
                  Showing {filteredRequests.length} of {requests.length} requests
                </small>
              </div>

              <div className="app-table-wrap table-responsive">
                <table className="table table-striped table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Property</th>
                      <th>Date</th>
                      <th>Location</th>
                      <th>Permission</th>
                      <th>Assigned Tech</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Problem Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          <div className="d-inline-flex align-items-center gap-2 justify-content-center flex-wrap">
                            <span className="spinner-border spinner-border-sm" role="status" aria-label="Loading" />
                            <span>Loading service requests…</span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredRequests.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4 small app-empty-hint">
                          {requests.length === 0 ? 'No service requests found' : 'No requests match your filters'}
                        </td>
                      </tr>
                    ) : (
                      filteredRequests.map((request) => (
                        <tr key={request.id}>
                          <td>{getPropertyName(request.property_id)}</td>
                          <td>{formatDate(request.date)}</td>
                          <td>{request.location || 'N/A'}</td>
                          <td>
                            {request.permission ? (
                              <span className="badge bg-success">Yes</span>
                            ) : (
                              <span className="badge bg-secondary">No</span>
                            )}
                          </td>
                          <td>{request.tech_name || 'Unassigned'}</td>
                          <td>
                            <span className={getPriorityBadgeClass(request.priority)}>
                              {request.priority || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className={getStatusBadgeClass(request.status)}>
                              {request.status || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={request.problem}>
                              {request.problem || 'No description'}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2" role="group">
                              <button 
                                className="btn btn-primary"
                                onClick={() => navigate(serviceRequestDetail(request.id))}
                              >
                                Details
                              </button>
                              <button 
                                className="btn btn-warning"
                                onClick={() => navigate(serviceRequestEdit(request.id))}
                              >
                                Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card app-card">
            <div className="card-body">
              <h5 className="card-title">Recent Activity</h5>
              <p className="card-text">No recent activity to display.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 